// Settings → Profile avatar uploader. Three-step flow per file:
//   1. POST /settings/profile/avatar/sign     → presigned PUT URL
//   2. PUT bytes directly to the presigned URL
//   3. POST /settings/profile/avatar/commit   → returns the replacement
//      <div id="profile-avatar"> for in-place swap.
// "Remove photo" hits DELETE /settings/profile/avatar and swaps in the
// initials-bubble fallback the server returns.

import { onceGuard } from "./bind_once.js";
import { request, signAndPut } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-profile-avatar]";
const FALLBACK_UPLOAD_ERROR = "Could not upload your photo. Please try again.";
const FALLBACK_REMOVE_ERROR = "Could not remove your photo. Please try again.";

export function initProfileAvatar(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const input = widget.querySelector("[data-profile-avatar-input]");
  const removeBtn = widget.querySelector("[data-profile-avatar-remove]");
  const errorEl = widget.querySelector("[data-profile-avatar-error]");
  const statusEl = widget.querySelector("[data-profile-avatar-status]");

  if (input) {
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;

      hide(errorEl);
      setStatus(statusEl, "Uploading…");
      input.disabled = true;

      try {
        const fragmentHTML = await uploadOne(file);
        replaceAvatar(widget, fragmentHTML);
      } catch (err) {
        console.error("profile avatar upload failed", err);
        showError(errorEl, err.userMessage || FALLBACK_UPLOAD_ERROR);
      } finally {
        hide(statusEl);
        input.disabled = false;
        input.value = "";
      }
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener("click", async () => {
      hide(errorEl);
      removeBtn.disabled = true;
      setStatus(statusEl, "Removing…");

      try {
        const resp = await request("/settings/profile/avatar", {
          method: "DELETE",
          accept: "text/html",
        });
        const fragmentHTML = await resp.text();
        replaceAvatar(widget, fragmentHTML);
      } catch (err) {
        console.error("profile avatar removal failed", err);
        removeBtn.disabled = false;
        showError(errorEl, err.userMessage || FALLBACK_REMOVE_ERROR);
      } finally {
        hide(statusEl);
      }
    });
  }
}

async function uploadOne(file) {
  const signed = await signAndPut("/settings/profile/avatar/sign", file);

  const committed = await request("/settings/profile/avatar/commit", {
    fields: { key: signed.key },
    accept: "text/html",
  });

  return committed.text();
}

function replaceAvatar(widget, fragmentHTML) {
  const avatar = widget.querySelector("#profile-avatar");
  if (!avatar) return;
  avatar.outerHTML = fragmentHTML;
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hide(el) {
  if (!el) return;
  el.textContent = "";
  el.classList.add("hidden");
}

function setStatus(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}
